/**
 * Safe HTML rendering helper.
 *
 * All translation strings we render with `dangerouslySetInnerHTML` are loaded
 * from our static i18n JSON files (`/app/frontend/src/i18n/locales/`), not
 * from user input — so they are trusted by construction.
 *
 * However, we still pipe them through DOMPurify so that:
 *   (a) If a future PR mixes user-provided text into a t() value (e.g. via
 *       interpolation of a username), no script tags can ever land in the DOM.
 *   (b) We strip unexpected attributes that may sneak in (onerror, javascript:
 *       hrefs) — defense-in-depth, even if the strings are static today.
 *
 * `ALLOWED_TAGS` is intentionally narrow — only the formatting tags our
 * translations actually use. Anything else (script, iframe, style, object,
 * embed, …) is stripped silently.
 */
import DOMPurify from "dompurify";

const ALLOWED_TAGS = ["strong", "b", "em", "i", "a", "br", "span", "u"];
const ALLOWED_ATTR = ["href", "target", "rel", "class"];

/**
 * Returns an object compatible with React's `dangerouslySetInnerHTML` prop
 * with the input sanitised through DOMPurify.
 *
 * Usage:
 *   <p dangerouslySetInnerHTML={safeHtml(t("preview.footer"))} />
 */
export function safeHtml(raw) {
  if (raw == null) return { __html: "" };
  const clean = DOMPurify.sanitize(String(raw), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    USE_PROFILES: { html: true },
  });
  return { __html: clean };
}

export default safeHtml;
